// DbQueryTab — pestaña superadmin para correr SELECTs read-only contra la
// DB del backend. Pensada para diagnóstico operativo: ver el `last_error`
// de un `stays_events` atascado, contar filas de una tabla, validar que
// un booking se haya creado, etc. — sin tener que entrar al psql de
// Railway.
//
// SEGURIDAD (3 capas, ver docs/diagnostico-sql.md):
//   1. JWT + cookie HttpOnly (verifyToken backend).
//   2. requireRole("superadministrador") en admin.routes.js.
//   3. Password compartido (env DB_QUERY_PASSWORD en Railway): se pide
//      al entrar al tab, se persiste en sessionStorage (no localStorage:
//      muere con la pestaña), viaja por header `x-db-query-password`.
//
// Si el backend devuelve 403 INVALID_DB_QUERY_PASSWORD reseteamos la
// password almacenada y volvemos a pedirla. Si 503 FEATURE_DISABLED,
// significa que la env no está seteada en Railway y mostramos
// instrucciones (sin permitir queries).
//
// Backend: POST /api/admin/db-query (backend/src/controllers/
// dbQuery.controller.js). Capas server-side adicionales: allowlist
// verbo inicial, blocklist keywords mutantes, READ ONLY transaction,
// statement_timeout=8s, row cap 500. Auditoría automática vía middleware
// adminAudit. El password NO se persiste en el audit log (viaja por
// header, no por body).
//
// Round 2026-05-28 — diagnóstico bug "bookings de webhook no aparecen
// en el calendario". Pedido del owner para debuggear sin exponer
// credenciales de DB.

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  PlayCircleOutlined,
  ClockCircleOutlined,
  LockOutlined,
  CopyOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { getMsg } from "../../utils/antdStatic";
import { API_BASE_URL, getCsrfToken } from "../../services/apiClient";
import { paginacionCliente } from "../../utils/tablaPaginacion";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// sessionStorage key — vive solo mientras la pestaña esté abierta.
// Al cerrar el navegador / abrir nueva pestaña se vuelve a pedir.
const SS_KEY = "db_query_password";

// Queries de ejemplo — copiables al editor con un click. Útiles para
// orientar a un admin nuevo y para que el caso de uso recurrente
// (debug de Stays) no requiera memorizar SQL.
const SAMPLE_QUERIES: { label: string; sql: string; description: string }[] = [
  {
    label: "Estado de eventos de Stays atascados",
    description:
      "Muestra eventos en queued/failed con su last_error — donde mueren los webhooks que no generan booking.",
    sql: `SELECT entity_key, action_type, status, attempts, last_error, last_dt, next_attempt_at
  FROM stays_events
 WHERE status IN ('queued', 'processing', 'failed')
 ORDER BY last_dt DESC
 LIMIT 50`,
  },
  {
    label: "Booking por ID corto de Stays",
    description:
      "Reemplaza KN16J por el entity_key de Stays. Si devuelve 0 filas, el booking no se creó.",
    sql: `SELECT id, id_reserval, id_propiedad, ingreso, salida, plataforma, nombre_huesped
  FROM reservas
 WHERE id_reserval = 'KN16J'`,
  },
  {
    label: "Últimos webhooks logueados (firehose)",
    description: "Mira lo mismo que /stays-webhooks pero crudo desde la tabla.",
    sql: `SELECT id, received_at, outcome, http_status, action, entity_key, error_code
  FROM stays_webhook_logs
 ORDER BY received_at DESC
 LIMIT 50`,
  },
  {
    label: "Propiedades con habilitado = false",
    description:
      "Si una propiedad está deshabilitada, el webhook de Stays la ignora silenciosamente (stays.service.js).",
    sql: `SELECT id, nombre_propiedad, id_propiedad_largo, habilitado, lavanderia_id
  FROM propiedades
 WHERE habilitado = false OR habilitado IS NULL
 ORDER BY id`,
  },
  {
    label: "Listar tablas del schema",
    description: "Para descubrir qué hay disponible.",
    sql: `SELECT table_name
  FROM information_schema.tables
 WHERE table_schema = 'public'
 ORDER BY table_name`,
  },
];

interface QueryResult {
  rows: Record<string, unknown>[];
  fields: string[];
  rowCount: number;
  rawRowCount: number;
  truncated: boolean;
  durationMs: number;
}

const DbQueryTab: React.FC = () => {
  const [sql, setSql] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Password: se intenta hidratar de sessionStorage al montar. Si no
  // hay, se abre el modal. Nunca se persiste en localStorage.
  const [password, setPassword] = useState<string | null>(null);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdInput, setPwdInput] = useState("");
  // Sentinel para mostrar la instrucción si el backend devuelve
  // FEATURE_DISABLED (env no seteada en Railway).
  const [featureDisabled, setFeatureDisabled] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(SS_KEY);
    if (stored) {
      setPassword(stored);
    } else {
      setPwdModalOpen(true);
    }
  }, []);

  const submitPassword = useCallback(() => {
    const trimmed = pwdInput.trim();
    if (!trimmed) {
      getMsg().warning("La contraseña no puede estar vacía");
      return;
    }
    sessionStorage.setItem(SS_KEY, trimmed);
    setPassword(trimmed);
    setPwdInput("");
    setPwdModalOpen(false);
  }, [pwdInput]);

  const clearPassword = useCallback(() => {
    sessionStorage.removeItem(SS_KEY);
    setPassword(null);
    setPwdModalOpen(true);
  }, []);

  const handleRun = useCallback(async () => {
    const trimmed = sql.trim();
    if (!trimmed) {
      getMsg().warning("Escribe una consulta primero");
      return;
    }
    if (!password) {
      setPwdModalOpen(true);
      return;
    }
    setRunning(true);
    setError(null);
    try {
      // Usamos fetch crudo en lugar de apiFetch para poder distinguir
      // los 3 outcomes: 200 OK / 403 password wrong / 503 feature off.
      // apiFetch nos ocultaría el status detrás de toasts genéricos.
      // Headers obligatorios: Content-Type, x-db-query-password y
      // X-CSRF-Token (round 2026-04-26 fase 6 — el backend rechaza
      // mutaciones sin CSRF aunque vengan con cookie JWT válida).
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "x-db-query-password": password,
      };
      const csrf = getCsrfToken();
      if (csrf) headers["X-CSRF-Token"] = csrf;

      const res = await fetch(`${API_BASE_URL}/admin/db-query`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ sql: trimmed }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 503 && json?.error?.code === "FEATURE_DISABLED") {
        setFeatureDisabled(true);
        setError(json.error.message);
        return;
      }
      if (res.status === 403 && json?.error?.code === "INVALID_DB_QUERY_PASSWORD") {
        clearPassword();
        setError("Contraseña de diagnóstico SQL inválida. Vuelve a ingresarla.");
        return;
      }
      if (!res.ok || !json?.success) {
        setError(json?.error?.message ?? `Error ${res.status}`);
        return;
      }
      setResult(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error de red");
    } finally {
      setRunning(false);
    }
  }, [sql, password, clearPassword]);

  // Copia el resultado entero (rows + meta) al clipboard como JSON
  // indentado. Útil para pegárselo a Claude / un compañero / un ticket.
  // Captura la SQL ejecutada y los timings para que el receptor tenga
  // todo el contexto sin volver a correr la query.
  const handleCopyJson = useCallback(async () => {
    if (!result) return;
    const payload = {
      sql: sql.trim(),
      executedAt: new Date().toISOString(),
      durationMs: result.durationMs,
      rowCount: result.rowCount,
      rawRowCount: result.rawRowCount,
      truncated: result.truncated,
      fields: result.fields,
      rows: result.rows,
    };
    const text = JSON.stringify(payload, null, 2);
    try {
      // clipboard API requiere contexto secure (https / localhost) —
      // ambas condiciones se cumplen en prod y dev. Si fallara igual,
      // fallback al textarea-trick clásico abajo.
      await navigator.clipboard.writeText(text);
      getMsg().success("JSON copiado al portapapeles");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        getMsg().success("JSON copiado al portapapeles");
      } catch {
        getMsg().error("No se pudo copiar — el navegador bloqueó el clipboard.");
      } finally {
        document.body.removeChild(ta);
      }
    }
  }, [result, sql]);

  // Descarga el resultado como un archivo .json. Mismo payload que el
  // copy (sql + meta + rows) para que el archivo sea auto-contenido.
  // Nombre: `db-query-<YYYY-MM-DD_HH-MM-SS>.json` (sin timezone, en
  // hora local del cliente — el cron lo decide a partir del executedAt
  // dentro del JSON si necesitás precisión).
  const handleDownloadJson = useCallback(() => {
    if (!result) return;
    const payload = {
      sql: sql.trim(),
      executedAt: new Date().toISOString(),
      durationMs: result.durationMs,
      rowCount: result.rowCount,
      rawRowCount: result.rawRowCount,
      truncated: result.truncated,
      fields: result.fields,
      rows: result.rows,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    a.href = url;
    a.download = `db-query-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [result, sql]);

  // Permitir Ctrl+Enter para ejecutar — patrón estándar en editores SQL.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleRun();
    }
  };

  // Renderizado de celda: stringify para JSONB/objetos y null-as-italic.
  // Tablas como `stays_events.payload` son JSONB — sin esto se renderiza
  // como "[object Object]".
  const renderCell = (value: unknown): React.ReactNode => {
    if (value === null) return <Text type="secondary" italic>null</Text>;
    if (value === undefined) return <Text type="secondary" italic>—</Text>;
    if (typeof value === "object") {
      return (
        <Text code style={{ fontSize: 12 }}>
          {JSON.stringify(value).slice(0, 200)}
        </Text>
      );
    }
    if (typeof value === "boolean") return <Tag color={value ? "green" : "red"}>{String(value)}</Tag>;
    return String(value);
  };

  const columns = result?.fields.map((field) => ({
    title: field,
    dataIndex: field,
    key: field,
    ellipsis: true,
    render: renderCell,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Alert
        message="Solo SELECT (read-only)"
        description={
          <span>
            Esta pestaña ejecuta SQL contra la DB en una transacción READ ONLY
            con timeout de 8s y cap de 500 filas. Sin INSERT/UPDATE/DELETE/DDL.
            Requiere password de diagnóstico (env <code>DB_QUERY_PASSWORD</code> en Railway).
            Cada query se audita en <code>admin_audit</code> con tu usuario.
          </span>
        }
        type="info"
        showIcon
        action={
          password ? (
            <Button size="small" icon={<LockOutlined />} onClick={clearPassword}>
              Olvidar password
            </Button>
          ) : null
        }
      />

      {featureDisabled && (
        <Alert
          message="Endpoint desactivado"
          description={
            <div>
              <Paragraph style={{ marginBottom: 8 }}>
                El backend no tiene <code>DB_QUERY_PASSWORD</code> configurada (o tiene menos de 8 chars).
                La feature está apagada por seguridad (fail-closed).
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                Para habilitarla: Railway → servicio backend → Variables → agregar{" "}
                <code>DB_QUERY_PASSWORD</code> con una password fuerte → redeploy.
              </Paragraph>
            </div>
          }
          type="warning"
          showIcon
        />
      )}

      <Card size="small" title="Editor SQL">
        <TextArea
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="SELECT * FROM stays_events WHERE status = 'failed' ORDER BY last_dt DESC LIMIT 20"
          rows={6}
          style={{ fontFamily: "monospace", fontSize: 13 }}
          maxLength={5000}
          showCount
        />
        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleRun}
              loading={running}
              disabled={!sql.trim() || featureDisabled}
            >
              Ejecutar
            </Button>
            <Button onClick={() => { setSql(""); setResult(null); setError(null); }}>
              Limpiar
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyJson}
              disabled={!result}
              title="Copia el resultado (sql + meta + rows) al portapapeles como JSON"
            >
              Copiar JSON
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadJson}
              disabled={!result}
              title="Descarga el resultado como archivo .json"
            >
              Descargar
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Ctrl+Enter para ejecutar
            </Text>
          </Space>
          {result && (
            <Text type="secondary">
              <ClockCircleOutlined /> {result.durationMs} ms · {result.rowCount}
              {result.truncated && ` de ${result.rawRowCount} (truncado)`} filas
            </Text>
          )}
        </div>
      </Card>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      <Card size="small" title="Queries de ejemplo">
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          {SAMPLE_QUERIES.map((sample) => (
            <div
              key={sample.label}
              style={{
                padding: 8,
                border: "1px solid #f0f0f0",
                borderRadius: 4,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <Text strong>{sample.label}</Text>
                  <Paragraph type="secondary" style={{ marginBottom: 4, fontSize: 12 }}>
                    {sample.description}
                  </Paragraph>
                </div>
                <Button size="small" onClick={() => setSql(sample.sql)}>
                  Usar
                </Button>
              </div>
            </div>
          ))}
        </Space>
      </Card>

      {result && result.rows.length > 0 && (
        <Card size="small" title={`Resultado (${result.rowCount} ${result.rowCount === 1 ? "fila" : "filas"})`}>
          {result.truncated && (
            <Alert
              style={{ marginBottom: 12 }}
              message={`Resultado truncado a ${result.rowCount} filas (la query devolvió ${result.rawRowCount}).`}
              type="warning"
              showIcon
            />
          )}
          <Table
            columns={columns}
            dataSource={result.rows.map((r, i) => ({ ...r, __rowKey: i }))}
            rowKey="__rowKey"
            scroll={{ x: "max-content" }}
            size="small"
            pagination={paginacionCliente({ porDefecto: 50, etiqueta: "fila" })}
          />
        </Card>
      )}

      {result && result.rows.length === 0 && (
        <Alert
          message="0 filas"
          description="La query se ejecutó correctamente pero no devolvió resultados."
          type="success"
          showIcon
        />
      )}

      <Modal
        title={
          <span>
            <LockOutlined /> Contraseña de diagnóstico SQL
          </span>
        }
        open={pwdModalOpen}
        onOk={submitPassword}
        onCancel={() => setPwdModalOpen(false)}
        okText="Guardar"
        cancelText="Cancelar"
        destroyOnClose
        maskClosable={false}
      >
        <Paragraph type="secondary">
          Ingresa la contraseña configurada en Railway como <code>DB_QUERY_PASSWORD</code>.
          Se guarda en <code>sessionStorage</code> (se borra al cerrar la pestaña).
        </Paragraph>
        <Form layout="vertical" onFinish={submitPassword}>
          <Form.Item label="Contraseña">
            <Input.Password
              value={pwdInput}
              onChange={(e) => setPwdInput(e.target.value)}
              autoFocus
              placeholder="Mín. 8 chars"
              onPressEnter={submitPassword}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DbQueryTab;
